import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { randomRange } from "../../../common/common_funcs.js";
import { VfxErodingBillboardAdditiveBatch, VfxErodingBillboardNormalBatch } from "../../core/batches/vfx_eroding_billboard_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { VfxGradientAtlas } from "../../core/textures/vfx_gradient_atlas.js";

export class VfxCannonFireTask implements IVfxTask
{
  private static fire_gradient_id: number = -1;
  private static smoke_gradient_id: number = -1;

  private position    : THREE.Vector3;
  private direction   : THREE.Vector3;
  private start_time  : number;
  private has_spawned : boolean = false;

  // --- Cannon Fire Particles Constants (ResourceID.Fire + CloudNoiseTexture) ---
  private static readonly CF_FIRE_CORE_COUNT       : number      = 6;
  private static readonly CF_FIRE_PLUME_COUNT      : number      = 8;
  private static readonly CF_FIRE_BASE_COUNT       : number      = 4;
  private static readonly CF_FIRE_MIN_LIFESPAN     : number      = 0.25;
  private static readonly CF_FIRE_MAX_LIFESPAN     : number      = 0.35;
  private static readonly CF_FIRE_DISSOLVE_START   : number      = 0.15;
  private static readonly CF_FIRE_COLOR_1          : THREE.Color = new THREE.Color( 0xFFDC50 );
  private static readonly CF_FIRE_COLOR_2          : THREE.Color = new THREE.Color( 0xFF8C3C );
  private static readonly CF_FIRE_END_RATIO        : number      = 0.5;
  private static readonly CF_FIRE_DISSOLVE_SOFTNESS: number      = 0.6;

  // Core jet (центр спалаху — найшвидший струмінь)
  private static readonly CF_CORE_MIN_SPEED        : number = 6;
  private static readonly CF_CORE_MAX_SPEED        : number = 8;
  private static readonly CF_CORE_MAX_SPREAD       : number = 0.5;
  private static readonly CF_CORE_MIN_START_SCALE  : number = 0.2;
  private static readonly CF_CORE_MAX_START_SCALE  : number = 0.3;
  private static readonly CF_CORE_MIN_END_SCALE    : number = 0.65;
  private static readonly CF_CORE_MAX_END_SCALE    : number = 0.8;

  // Conical plume (конічне розширення тіла полум'я)
  private static readonly CF_PLUME_MIN_SPEED       : number = 2;
  private static readonly CF_PLUME_MAX_SPEED       : number = 3.5;
  private static readonly CF_PLUME_MAX_SPREAD      : number = 1.2;
  private static readonly CF_PLUME_MIN_START_SCALE : number = 0.25;
  private static readonly CF_PLUME_MAX_START_SCALE : number = 0.35;
  private static readonly CF_PLUME_MIN_END_SCALE   : number = 0.9;
  private static readonly CF_PLUME_MAX_END_SCALE   : number = 1;

  // Muzzle root (основа полум'я біля зрізу ствола)
  private static readonly CF_BASE_MIN_SPEED        : number = 1;
  private static readonly CF_BASE_MAX_SPEED        : number = 2;
  private static readonly CF_BASE_MAX_SPREAD       : number = 0.6;
  private static readonly CF_BASE_MIN_START_SCALE  : number = 0.3;
  private static readonly CF_BASE_MAX_START_SCALE  : number = 0.4;
  private static readonly CF_BASE_MIN_END_SCALE    : number = 0.7;
  private static readonly CF_BASE_MAX_END_SCALE    : number = 0.9;

  // --- Cannon Smoke Constants ---
  private static readonly CF_SMOKE_COUNT             : number      = 10;
  private static readonly CF_SMOKE_MIN_LIFESPAN      : number      = 0.35;
  private static readonly CF_SMOKE_MAX_LIFESPAN      : number      = 0.45;
  private static readonly CF_SMOKE_MIN_SPEED         : number      = 0.2;
  private static readonly CF_SMOKE_MAX_SPEED         : number      = 1.5;
  private static readonly CF_SMOKE_MAX_SPREAD        : number      = 1.5;
  private static readonly CF_SMOKE_MIN_START_SCALE   : number      = 0.3;
  private static readonly CF_SMOKE_MAX_START_SCALE   : number      = 0.5;
  private static readonly CF_SMOKE_MIN_END_SCALE     : number      = 0.9;
  private static readonly CF_SMOKE_MAX_END_SCALE     : number      = 1;
  private static readonly CF_SMOKE_COLOR             : THREE.Color = new THREE.Color( 0xFFFFFF );

  private right_vec: THREE.Vector3;
  private up_vec: THREE.Vector3;
  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();

  public constructor( position: THREE.Vector3, direction: THREE.Vector3 )
  {
    this.position   = position.clone();
    this.direction  = direction.clone().normalize();
    this.start_time = Vfx3DRuntime.current_time;

    const temp_vec = Math.abs( this.direction.y ) < 0.99 ? new THREE.Vector3( 0, 1, 0 ) : new THREE.Vector3( 1, 0, 0 );
    this.right_vec = new THREE.Vector3().crossVectors( this.direction, temp_vec ).normalize();
    this.up_vec    = new THREE.Vector3().crossVectors( this.right_vec, this.direction ).normalize();
  }

  private calculateVelocity( min_speed: number, max_speed: number, max_spread: number ): THREE.Vector3
  {
    const speed = randomRange( min_speed, max_speed );
    VfxCannonFireTask.scratch_vel.copy( this.direction ).multiplyScalar( speed );

    const spread_angle     = randomRange( 0, Math.PI * 2 );
    const spread_intensity = randomRange( 0, max_spread );
    VfxCannonFireTask.scratch_vel.addScaledVector( this.right_vec, Math.cos( spread_angle ) * spread_intensity );
    VfxCannonFireTask.scratch_vel.addScaledVector( this.up_vec, Math.sin( spread_angle ) * spread_intensity );

    return VfxCannonFireTask.scratch_vel;
  }

  private spawnFireParticles( fire_batch: VfxErodingBillboardAdditiveBatch ): void
  {
    if ( VfxCannonFireTask.fire_gradient_id === -1 ) {
      VfxCannonFireTask.fire_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxCannonFireTask.CF_FIRE_COLOR_1 },
        { stop: VfxCannonFireTask.CF_FIRE_END_RATIO, color: VfxCannonFireTask.CF_FIRE_COLOR_2 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) }
      ]);
    }

    // 1. Центральний швидкий струмінь (Core Jet)
    for ( let i = 0; i < VfxCannonFireTask.CF_FIRE_CORE_COUNT; i++ )
    {
      const vel = this.calculateVelocity(
        VfxCannonFireTask.CF_CORE_MIN_SPEED,
        VfxCannonFireTask.CF_CORE_MAX_SPEED,
        VfxCannonFireTask.CF_CORE_MAX_SPREAD
      );
      const start_scale = randomRange( VfxCannonFireTask.CF_CORE_MIN_START_SCALE, VfxCannonFireTask.CF_CORE_MAX_START_SCALE );
      const end_scale   = randomRange( VfxCannonFireTask.CF_CORE_MIN_END_SCALE, VfxCannonFireTask.CF_CORE_MAX_END_SCALE );
      const lifespan    = randomRange( VfxCannonFireTask.CF_FIRE_MIN_LIFESPAN, VfxCannonFireTask.CF_FIRE_MAX_LIFESPAN );
      VfxCannonFireTask.scratch_pos.copy( this.position ).addScaledVector( this.direction, randomRange( 0, 0.08 ) );

      fire_batch.spawnParticle(
        ResourceID.Fire,
        ResourceID.CloudNoiseTexture,
        VfxCannonFireTask.fire_gradient_id,
        VfxCannonFireTask.scratch_pos,
        vel,
        start_scale,
        end_scale,
        this.start_time,
        lifespan,
        VfxCannonFireTask.CF_FIRE_DISSOLVE_START,
        VfxCannonFireTask.CF_FIRE_DISSOLVE_SOFTNESS,
        randomRange( 0, Math.PI * 2 )
      );
    }

    // 2. Конічний розширений шлейф (Conical Plume)
    for ( let i = 0; i < VfxCannonFireTask.CF_FIRE_PLUME_COUNT; i++ )
    {
      const vel = this.calculateVelocity(
        VfxCannonFireTask.CF_PLUME_MIN_SPEED,
        VfxCannonFireTask.CF_PLUME_MAX_SPEED,
        VfxCannonFireTask.CF_PLUME_MAX_SPREAD
      );
      const start_scale = randomRange( VfxCannonFireTask.CF_PLUME_MIN_START_SCALE, VfxCannonFireTask.CF_PLUME_MAX_START_SCALE );
      const end_scale   = randomRange( VfxCannonFireTask.CF_PLUME_MIN_END_SCALE, VfxCannonFireTask.CF_PLUME_MAX_END_SCALE );
      const lifespan    = randomRange( VfxCannonFireTask.CF_FIRE_MIN_LIFESPAN, VfxCannonFireTask.CF_FIRE_MAX_LIFESPAN );
      VfxCannonFireTask.scratch_pos.copy( this.position ).addScaledVector( this.direction, randomRange( 0, 0.05 ) );

      fire_batch.spawnParticle(
        ResourceID.Fire,
        ResourceID.CloudNoiseTexture,
        VfxCannonFireTask.fire_gradient_id,
        VfxCannonFireTask.scratch_pos,
        vel,
        start_scale,
        end_scale,
        this.start_time,
        lifespan,
        VfxCannonFireTask.CF_FIRE_DISSOLVE_START,
        VfxCannonFireTask.CF_FIRE_DISSOLVE_SOFTNESS,
        randomRange( 0, Math.PI * 2 )
      );
    }

    // 3. Основа полум'я біля зрізу ствола (Muzzle Base)
    for ( let i = 0; i < VfxCannonFireTask.CF_FIRE_BASE_COUNT; i++ )
    {
      const vel = this.calculateVelocity(
        VfxCannonFireTask.CF_BASE_MIN_SPEED,
        VfxCannonFireTask.CF_BASE_MAX_SPEED,
        VfxCannonFireTask.CF_BASE_MAX_SPREAD
      );
      const start_scale = randomRange( VfxCannonFireTask.CF_BASE_MIN_START_SCALE, VfxCannonFireTask.CF_BASE_MAX_START_SCALE );
      const end_scale   = randomRange( VfxCannonFireTask.CF_BASE_MIN_END_SCALE, VfxCannonFireTask.CF_BASE_MAX_END_SCALE );
      const lifespan    = randomRange( VfxCannonFireTask.CF_FIRE_MIN_LIFESPAN * 0.9, VfxCannonFireTask.CF_FIRE_MAX_LIFESPAN * 0.9 );

      fire_batch.spawnParticle(
        ResourceID.Fire,
        ResourceID.CloudNoiseTexture,
        VfxCannonFireTask.fire_gradient_id,
        this.position,
        vel,
        start_scale,
        end_scale,
        this.start_time,
        lifespan,
        VfxCannonFireTask.CF_FIRE_DISSOLVE_START,
        VfxCannonFireTask.CF_FIRE_DISSOLVE_SOFTNESS,
        randomRange( 0, Math.PI * 2 )
      );
    }
  }

  private spawnSmokeParticles( smoke_batch: VfxErodingBillboardNormalBatch ): void
  {
    if ( VfxCannonFireTask.smoke_gradient_id === -1 ) {
      VfxCannonFireTask.smoke_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxCannonFireTask.CF_SMOKE_COLOR },
        { stop: 1.0, color: VfxCannonFireTask.CF_SMOKE_COLOR }
      ]);
    }

    for ( let i = 0; i < VfxCannonFireTask.CF_SMOKE_COUNT; i++ )
    {
      const vel = this.calculateVelocity(
        VfxCannonFireTask.CF_SMOKE_MIN_SPEED,
        VfxCannonFireTask.CF_SMOKE_MAX_SPEED,
        VfxCannonFireTask.CF_SMOKE_MAX_SPREAD
      );
      const start_scale = randomRange( VfxCannonFireTask.CF_SMOKE_MIN_START_SCALE, VfxCannonFireTask.CF_SMOKE_MAX_START_SCALE );
      const end_scale   = randomRange( VfxCannonFireTask.CF_SMOKE_MIN_END_SCALE, VfxCannonFireTask.CF_SMOKE_MAX_END_SCALE );
      const lifespan    = randomRange( VfxCannonFireTask.CF_SMOKE_MIN_LIFESPAN, VfxCannonFireTask.CF_SMOKE_MAX_LIFESPAN );

      smoke_batch.spawnParticle(
        ResourceID.Smog,
        ResourceID.CloudNoiseTexture,
        VfxCannonFireTask.smoke_gradient_id,
        this.position,
        vel,
        start_scale,
        end_scale,
        this.start_time,
        lifespan,
        0.3, // dissolve start progress
        0.9, // dissolve_softness
        randomRange( 0, Math.PI * 2 )
      );
    }
  }

  public onUpdate( now: number ): boolean
  {
    if ( now < this.start_time )
      return false;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;

      const fire_batch = Vfx3DRuntime.getBatch( VfxErodingBillboardAdditiveBatch );
      const smoke_batch = Vfx3DRuntime.getBatch( VfxErodingBillboardNormalBatch );

      // Fire
      this.spawnFireParticles( fire_batch );
      // Smoke
      this.spawnSmokeParticles( smoke_batch );
    }

    return true;
  }
}
